import { useEffect, useState, useRef, useCallback, useContext } from "react";
import Navbar from "shared/components/layout/Navbar";
import Footer from "shared/components/layout/Footer";
import { useTranslation } from 'context/LanguageContext';
import Categories from "features/categories/components/Categories";
import HomeSlider from "features/home/components/HomeSlider";
import WhatsAppButton from "features/home/components/WhatsAppButton";
import { getProductsByCategoryId } from "api/repository/products.api";
import ProductCard from "features/products/components/ProductCard";
import FeaturedCategories from "features/categories/components/FeaturedCategories";
import { HomeContext } from "context/HomeContext";

const LIMIT = 20;

export default function HomePage() {
  const { language } = useTranslation();
  const isRTL = language === 'ar';
  const { featuredProducts, categories } = useContext(HomeContext);

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [products, setProducts] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [productsError, setProductsError] = useState(null);

  const lastProductRef = useRef();

  // Fetch products when category or offset changes
  useEffect(() => {
    if (!selectedCategoryId) return;
    let isActive = true;

    const fetchProducts = async () => {
      try {
        offset === 0 ? setLoadingProducts(true) : setLoadingMore(true);
        setProductsError(null);

        const response = await getProductsByCategoryId(selectedCategoryId, {
          limit: LIMIT,
          offset,
        });

        const newProducts = response?.data ?? [];

        if (!isActive) return;
        setProducts(prev => offset === 0 ? newProducts : [...prev, ...newProducts]);
        setHasMore(newProducts.length === LIMIT);
      } catch (err) {
        if (!isActive) return;
        console.error("Error fetching products:", err);
        setProductsError("Failed to load products.");
      } finally {
        if (!isActive) return;
        setLoadingProducts(false);
        setLoadingMore(false);
      }
    };

    fetchProducts();
    return () => {
      isActive = false;
    };
  }, [selectedCategoryId, offset]);

  // When a new category is selected, reset everything
  const handleCategorySelect = (categoryId) => {
    setSelectedCategoryId(categoryId);
    setProducts([]);
    setOffset(0);
    setHasMore(false);
    setProductsError(null);
    setLoadingProducts(false);
    setLoadingMore(false);
  };

  // Infinite scroll observer
  const handleObserver = useCallback((entries) => {
    if (entries[0].isIntersecting && hasMore && !loadingProducts && !loadingMore) {
      setOffset(prev => prev + LIMIT);
    }
  }, [hasMore, loadingProducts, loadingMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: "200px",
      threshold: 0,
    });
    if (lastProductRef.current) observer.observe(lastProductRef.current);
    return () => observer.disconnect();
  }, [handleObserver, products]);

  return (
    <div className="min-h-screen flex flex-col" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <Navbar />
        <Categories
          categories={categories}
          onCategorySelect={handleCategorySelect}
        />
      </div>

      <div className="pt-[130px]">
        <div className="mt-4 mb-2 relative z-30">
          <HomeSlider />
        </div>
        {!selectedCategoryId && (
          <div className="mt-4 relative z-20">
            <FeaturedCategories featuredSubcategories={featuredProducts} />
          </div>
        )}
        {/* Products Section */}
        {selectedCategoryId && (
          <div className="max-w-7xl mx-auto px-4 mt-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {categories.find(c => c.id === selectedCategoryId)?.name || ''}
            </h2>

            {/* Loading */}
            {loadingProducts && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
              </div>
            )}

            {/* Error */}
            {productsError && !loadingProducts && (
              <div className="text-center py-12">
                <p className="text-red-500">{productsError}</p>
              </div>
            )}

            {/* No Results */}
            {!loadingProducts && !productsError && products.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No products found in this category.</p>
              </div>
            )}

            {/* Grid */}
            {!loadingProducts && products.length > 0 && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {products.map((product, index) => (
                    <div
                      key={product.id}
                      className="cursor-pointer"
                      ref={index === products.length - 1 ? lastProductRef : null}
                    >
                      <ProductCard product={product} />
                    </div>
                  ))}
                </div>

                {loadingMore && (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
